import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, DrawerPresence, ErrorBanner, PageHeader } from '@zarya/ui';
import { t } from '@app/i18n';
import { useDrawerParam } from '@app/navigation';
import { useApprovalsSummary } from './Approvals.api';
import { ApprovalDrawer, type Decision } from './ApprovalDrawer';
import { HistoryTab } from './HistoryTab';
import { Kpi } from './Kpi';
import { PendingTab } from './PendingTab';
import s from './Approvals.module.css';

type Tab = 'pendentes' | 'historico';

/**
 * T9 — Aprovações (FR-057, FR-058, FR-068).
 *
 * "Só o que exige decisão humana: identidades novas descobertas no registry e
 * identidades cujas capabilities mudaram numa release. Release que só muda
 * código não passa por aqui."
 *
 * O drawer T10 abre na própria rota (`?drawer=<id>`), pela convenção do console.
 *
 * `?identidade=<uuid>` recorta a fila numa identidade só — é por onde chega quem
 * clicou «Ver aprovação pendente» no rodapé de T5 (30/08). O parâmetro vale nas
 * **duas** abas: quem veio de uma identidade quer o que está pendente e o que já
 * foi decidido dela, não a fila inteira.
 */
export function ApprovalsPage() {
  const [tab, setAba] = useState<Tab>('pendentes');
  const [decisao, setDecisao] = useState<Decision | null>(null);
  const drawer = useDrawerParam();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const summary = useApprovalsSummary();

  const identidade = searchParams.get('identidade');
  // Só o `identidade` sai: `?drawer=` pode estar na URL ao mesmo tempo (T10
  // aberto), e fechá-lo aqui seria efeito colateral de um clique que pede outra
  // coisa.
  const clearIdentity = () => {
    const proximos = new URLSearchParams(searchParams);
    proximos.delete('identidade');
    setSearchParams(proximos, { replace: true });
  };

  const payload = summary.data;

  return (
    <>
      <PageHeader
        title={t('approvals.page.title')}
        tip={t('approvals.page.purpose')}
        icon="flag"
      />

      {summary.isError ? (
        <ErrorBanner
          error={summary.error}
          title={t('approvals.page.error.summary')}
          onRetry={() => void summary.refetch()}
          hideLink
        />
      ) : null}

      {decisao ? (
        <Alert tone="info" title={decisao.mensagem}>
          {decisao.runtime ? (
            <>
              {t('approvals.page.decision.runtimeNote')}
              <div style={{ marginTop: 12 }}>
                <Button
                  variant="out"
                  size="sm"
                  icon="broadcast"
                  onClick={() => navigate('/runtime')}
                >
                  {t('approvals.page.decision.seeRuntime')}
                </Button>
              </div>
            </>
          ) : (
            t('approvals.page.decision.keptNote')
          )}
        </Alert>
      ) : null}

      <div className={s.kpis}>
        <Kpi
          label={t('approvals.page.kpi.pending')}
          valor={payload?.pending ?? 0}
          tone={payload?.pending ? 'danger' : 'success'}
          detalhe={t('approvals.page.kpi.pending.detail')}
          icon="pause"
        />
        <Kpi
          label={t('approvals.page.kpi.firstApproval')}
          valor={payload?.new_identities ?? 0}
          tone="primary"
          detalhe={t('approvals.page.kpi.firstApproval.detail')}
          icon="robot"
        />
        <Kpi
          label={t('approvals.page.kpi.changedCapabilities')}
          valor={payload?.changed_capabilities ?? 0}
          tone="warning"
          detalhe={t('approvals.page.kpi.changedCapabilities.detail')}
          icon="shield-alert"
        />
        <Kpi
          label={t('approvals.page.kpi.decided')}
          valor={payload?.decided ?? 0}
          tone="success"
          detalhe={t('approvals.page.kpi.decided.detail')}
          icon="check-circle"
        />
      </div>

      <Card bare>
        <div className={s.tabs} role="tablist" aria-label={t('approvals.page.tabs.label')}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'pendentes'}
            className={[s.tab, tab === 'pendentes' ? s.tabOn : ''].filter(Boolean).join(' ')}
            onClick={() => setAba('pendentes')}
          >
            {t('approvals.page.tab.pending', { n: payload?.pending ?? 0 })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'historico'}
            className={[s.tab, tab === 'historico' ? s.tabOn : ''].filter(Boolean).join(' ')}
            onClick={() => setAba('historico')}
          >
            {t('approvals.page.tab.history', { n: payload?.decided ?? 0 })}
          </button>
        </div>

        {tab === 'pendentes' ? (
          <PendingTab
            total={payload?.pending ?? 0}
            onAbrir={drawer.abrir}
            identityId={identidade}
            onLimparIdentidade={clearIdentity}
          />
        ) : (
          <HistoryTab
            total={payload?.decided ?? 0}
            onAbrir={drawer.abrir}
            identityId={identidade}
            onLimparIdentidade={clearIdentity}
          />
        )}
      </Card>

      {/* Montado só com o drawer aberto, para o `DrawerPresence` guardar o pedido que sai (aim-xz9). */}
      <DrawerPresence>
        {drawer.id ? (
          <ApprovalDrawer
            id={drawer.id}
            onFechar={drawer.fechar}
            onDecidido={(d) => {
              setDecisao(d);
              void summary.refetch();
            }}
          />
        ) : null}
      </DrawerPresence>
    </>
  );
}
